<template lang="pug">
  .c-coupon-list__item.c-coupon-list__item--selectable(:class="{selected: isSelected}")
    .c-coupon-list__item--clickable-area(@click="useCoupon", :disabled="!canUseCoupon")
      label.c-coupon(:class="couponClasses")
        .c-coupon__left
          .c-coupon__details.u-plxs.u-prxs
            // クーポン名
            .c-coupon__name
              p {{coupon.display_name | truncate(36)}}

            // Free Shippingクーポンの場合
            .c-coupon__values(v-if="coupon.is_shipping_free")
              .c-coupon__values-price
                | FREE STANDARD SHIPPING
                template(v-if="isAmountOffCoupon")
                  br
                  | $ {{coupon.amount_off}} OFF
                template(v-if="isPercentOffCoupon")
                  br
                  | {{coupon.percent_off}} % OFF

            // 割引クーポンの場合
            .c-coupon__values(v-else)
              template(v-if="isAmountOffCoupon")
                .c-coupon__values-prefix
                  | $
                .c-coupon__values-price(v-text="coupon.amount_off")
                .c-coupon__values-suffix
                  | OFF

              template(v-if="isPercentOffCoupon")
                .c-coupon__values-price(v-text="coupon.percent_off")
                .c-coupon__values-suffix
                  | %
                  br
                  | OFF

            // 最低利用金額
            .c-coupon__provision(v-if="coupon.minimum_use_amount")
              span
                | Valid on orders w/ subtotal over $
                | {{coupon.minimum_use_amount}}

          // プレミアム限定クーポン
          .c-coupon__type(v-if="isPremiumCoupon")
            img(:src="'/images/tom_premium_h_white.svg' | staticImageUrl", height="16")
            span.u-fs-sm.u-mlxs Exclusive

        .c-coupon__right
          // サマリー
          .c-coupon__summary(v-if="summaries.length")
            ul.c-list-note.u-color-white
              li(v-for="summary in summaries", v-text="summary")

          // 有効期限
          .c-coupon__period(v-if="discount.within_period")
            label Expires
            br
            span(v-text="discount.display_expires.datetime")

          // 開始日
          .c-coupon__period(v-else)
            label Start
            br
            span(v-text="discount.display_use_start.datetime")

      .c-coupon-list__item-check
        .form__checkbox
          input.form-ctl(
          type="checkbox",
          :value="discount._id",
          v-model="isSelected",
          :disabled="!canUseCoupon"
          )
          span.label Use this coupon

    // プレミアム会員限定クーポンの場合
    template(v-if="isPremiumCoupon && !isAvailableForPremium")
      // すでにプレミアム会員である、またはこれから加入しようとしている、自動更新後はクーポンが使用出来る可能性がある場合
      template(v-if="isPremiumMemberOrWillJoinPremium || willAvailableForPremiumService")
        p.u-color-red.u-mtxs.u-fs-sm
          | This coupon cannot be used due to the status of your TOM Premium membership. Please see details #[a(href="/orders/premium" target="_blank") here].
      // プレミアム会員でない、加入しようとしていない場合
      template(v-else)
        p.u-color-red.u-mtxs.u-fs-sm
          | Join TOM Premium to unlock this coupon. #[a(href="/premium" target="_blank") Learn more].

    // 最低利用金額を下回っている場合の注意文
    p.u-color-red.u-mtxs.u-fs-sm(v-if="displayExceedMinimuUseAmountError")
      | This coupon can only be used if the subtotal is equal to or over ${{coupon.minimum_use_amount || 0}}.
</template>

<script>
import intersection from 'latest_modules/lodash/intersection';

import { COUPON_TYPE, SALES_METHOD } from '../../constants/shop';
import { SERVICES } from '../../constants/service';
import { PLAN_TYPE } from '../../../../../../lib/document/schema/premium';

import urlUtil from '../../util/url';
import textUtil from '../../util/text';

import ProductSku from '../../stores/schema/ProductSku';

export default {
  name: 'CheckoutCoupon',

  props: {
    cart: {
      type: Object,
      required: true
    },

    discount: {
      type: Object,
      required: true
    },

    selectedCouponId: {
      type: String
    }
  },
  computed: {
    couponClasses() {
      return {
        'c-coupon--free-shipping': this.coupon.is_shipping_free,
        'c-coupon--percent-off': this.isPercentOffCoupon,
        'c-coupon--premium': this.isPremiumCoupon,
        'c-coupon--out-of-period': !this.isWithinPeriod,
        'c-coupon--disabled': !this.canUseCoupon,
        selected: this.isSelected
      };
    },

    user() {
      return this.$store.state.user;
    },

    shipments() {
      return this.cart.shipments;
    },

    coupon() {
      return this.$store.getters['shoppingCoupons/findById'](
        this.discount.coupon
      );
    },

    joinPremiumServiceId() {
      return this.$store.getters['premiumPlans/findOne']({
        _id: this.cart.premium.plan
      }).service;
    },

    subscribedPremiumServices() {
      return this.$store.getters[
        'shoppingDiscounts/getSubscribedPremiumServices'
      ];
    },

    subscribedAvailablePremiumServices() {
      return this.$store.getters[
        'shoppingDiscounts/getSubscribedAvailablePremiumServices'
      ];
    },

    // クーポンが使用できる期間内か否か
    isWithinPeriod() {
      return this.discount.within_period;
    },

    // クーポンが使用できるラベルIDを配列で返す
    labelCoupons() {
      return this.coupon.available_sku_conditions.labels || [];
    },

    // クーポンが使用できるサプライヤーIDを配列で返す
    supplierCoupons() {
      return this.coupon.available_sku_conditions.suppliers || [];
    },

    // 特定のラベル限定のクーポンか否か
    isLabelCoupon() {
      return !!this.labelCoupons.length;
    },

    // 特定のサプライヤー限定のクーポンか否か
    isSupplierCoupon() {
      return !!this.supplierCoupons.length;
    },

    // 在庫ありアイテム限定のクーポンか否か
    isInStockOnlyCoupon() {
      return this.coupon.in_stock_only;
    },

    // 割引クーポン（ドル）か否か
    isAmountOffCoupon() {
      return this.coupon.type === COUPON_TYPE.AMOUNT_OFF;
    },

    // 割引クーポン（割合）か否か
    isPercentOffCoupon() {
      return this.coupon.type === COUPON_TYPE.PERCENT_OFF;
    },

    // プレミアム会員限定クーポンか否か
    isPremiumCoupon() {
      return !!this.coupon.available_premium_services.length;
    },

    // ショップで使用できるクーポンか否か
    isAvailableForShop() {
      return this.coupon.available_services.includes(SERVICES.SHOP);
    },

    // サマリーを配列で返す
    summaries() {
      return this.coupon.summary ? this.coupon.summary.split('\n') : [];
    },

    // クーポンが最低金額を超えて使用可能か否か
    isExceedMinimumUseAmount() {
      let subtotal = 0;

      // ラベルクーポンの場合
      if (this.isLabelCoupon) {
        // ラベルクーポンの対象となるアイテムを抜き出して合計金額を算出
        this.shipments.forEach((shipment) => {
          shipment.items.forEach((item) => {
            if (intersection(item.labels, this.labelCoupons).length) {
              subtotal +=
                ProductSku.getSellingPrice(item.sku, this.user) * item.qty;
            }
          });
        });
      }
      // サプライヤークーポンの場合
      else if (this.isSupplierCoupon) {
        // サプライヤークーポンの対象となるアイテムを抜き出して合計金額を算出
        this.shipments.forEach((shipment) => {
          shipment.items.forEach((item) => {
            if (intersection(item.suppliers, this.supplierCoupons).length) {
              subtotal +=
                ProductSku.getSellingPrice(item.sku, this.user) * item.qty;
            }
          });
        });
      }
      // 在庫ありアイテム限定のクーポンの場合
      else if (this.isInStockOnlyCoupon) {
        // 在庫ありのアイテムを抜き出して合計金額を算出
        this.shipments.forEach((shipment) => {
          shipment.items.forEach((item) => {
            if (
              [SALES_METHOD.NORMAL, SALES_METHOD.CONSIGNMENT].includes(
                item.sku.sales_method
              )
            ) {
              subtotal +=
                ProductSku.getSellingPrice(item.sku, this.user) * item.qty;
            }
          });
        });
      }
      // 上記以外のクーポン
      else {
        subtotal = this.cart.order_subtotal;
      }

      return subtotal >= this.coupon.minimum_use_amount;
    },

    // プレミアム会員限定クーポンだった場合に使用できるプレミアムの条件を満たしているか否か
    isAvailableForPremium() {
      // そもそもプレミアム会員限定クーポンでなければ常に使用可能
      if (!this.isPremiumCoupon) {
        return true;
      }

      // クーポンがプレミアム入会者限定の場合に
      // プレミアム初回入会者限定クーポンなら初回入会か、
      // プレミアム再入会者限定クーポンなら再入会かをチェック
      if (this.couponAvailableJoiningPremiumTypes.length) {
        if (
          !this.willJoinPremium ||
          !(
            this.canUsePremiumFirstPlanLimitedCoupon ||
            this.canUsePremiumRecontractPlanLimitedCoupon
          )
        ) {
          return false;
        }
      }

      // プレミアム会員向けのチェック
      // クーポンが加入しているプレミアム会員のグレードで使用できるかをチェック
      if (this.isAvailableForPremiumService) {
        return true;
      }

      // 非プレミアム会員向けのチェック
      // クーポンが加入しようとしているプレミアム会員のグレードで使用できるかをチェック
      return (
        this.willJoinPremium &&
        this.couponAvailablePremiumServiceIds.includes(
          this.joinPremiumServiceId
        )
      );
    },

    // カートにラベルクーポンの対象となるアイテムが含まれているか否か
    isCartIncludeLabelItem() {
      return this.shipments.some((shipment) => {
        return shipment.items.some((item) => {
          return !!intersection(item.labels, this.labelCoupons).length;
        });
      });
    },

    // カートにサプライヤークーポンの対象となるアイテムが含まれているか否か
    isCartIncludeSupplierItem() {
      return this.shipments.some((shipment) => {
        return shipment.items.some((item) => {
          return !!intersection(item.suppliers, this.supplierCoupons).length;
        });
      });
    },

    // カートに在庫ありのアイテムが含まれているか否か
    isCartIncludeInStockItem() {
      return this.shipments.some((shipment) => {
        return shipment.items.some((item) => {
          return [SALES_METHOD.NORMAL, SALES_METHOD.CONSIGNMENT].includes(
            item.sku.sales_method
          );
        });
      });
    },

    // クーポンが使用できるか否か
    canUseCoupon() {
      return [
        this.isExceedMinimumUseAmount,
        this.isAvailableForShop,
        this.isAvailableForPremium,
        this.isWithinPeriod,
        // ラベルクーポンの場合はカートに対象となるラベルのアイテムがあるかをチェック
        this.isLabelCoupon ? this.isCartIncludeLabelItem : true,
        // サプライヤークーポンの場合はカートに対象となるサプライヤーのアイテムがあるかをチェック
        this.isSupplierCoupon ? this.isCartIncludeSupplierItem : true,
        // 在庫ありアイテム限定のクーポンの場合はカートに在庫ありのアイテムがあるかをチェック
        this.isInStockOnlyCoupon ? this.isCartIncludeInStockItem : true
      ].every((value) => {
        return value;
      });
    },

    // クーポンが選択されているか否か
    isSelected() {
      return this.selectedCouponId === this.discount._id && this.canUseCoupon;
    },

    // すでにプレミアム会員か否か
    isPremiumMember() {
      return this.user.is_premium_member;
    },

    // プレミアム会員になる予定か否か
    willJoinPremium() {
      return this.user.will_join_premium;
    },

    // 以前プレミアム会員だったか否か
    hadRegisteredPremiumInPast() {
      return this.user.had_registered_premium_in_past;
    },

    // プレミアム会員か、これからなる予定か否か
    isPremiumMemberOrWillJoinPremium() {
      return this.isPremiumMember || this.willJoinPremium;
    },

    // プレミアム初回入会者限定クーポンで初回入会か否か
    canUsePremiumFirstPlanLimitedCoupon() {
      return (
        this.couponAvailableJoiningPremiumTypes.includes(PLAN_TYPE.FIRST) &&
        !this.hadRegisteredPremiumInPast
      );
    },

    // プレミアム再入会者限定クーポンで再入会か否か
    canUsePremiumRecontractPlanLimitedCoupon() {
      return (
        this.couponAvailableJoiningPremiumTypes.includes(
          PLAN_TYPE.RECONTRACT
        ) && this.hadRegisteredPremiumInPast
      );
    },

    // クーポンが使用できるプレミアム入会タイプ（first or recontract）
    couponAvailableJoiningPremiumTypes() {
      // ローカルの古いクーポンにはavailable_joining_premium_typesが存在しない場合があったが、
      // Schema側でdefault値を設定するのでエラーは吐かないはず
      return this.coupon.available_joining_premium_types;
    },

    // クーポンが使用できるプレミアム会員のグレード（PremiumServicesのObjectId）を配列で返す
    couponAvailablePremiumServiceIds() {
      return this.coupon.available_premium_services.map((service) => {
        return service.toString();
      });
    },

    // ユーザーが加入しているプレミアム会員のグレード（PremiumServicesのObjectId）を配列で返す
    // ※満了日を過ぎて自動更新を待っているものを含む
    subscribedPremiumServiceIds() {
      return this.subscribedPremiumServices.map((service) => {
        return service._id.toString();
      });
    },

    // ユーザーが加入していて現在有効なプレミアム会員のグレード（PremiumServicesのObjectId）を配列で返す
    subscribedAvailablePremiumServiceIds() {
      return this.subscribedAvailablePremiumServices.map((service) => {
        return service._id.toString();
      });
    },

    // クーポンが加入しているプレミアム会員のグレードで使用できるか否か
    isAvailableForPremiumService() {
      return !!intersection(
        this.couponAvailablePremiumServiceIds,
        this.subscribedAvailablePremiumServiceIds
      ).length;
    },

    // クーポンが加入しているプレミアム会員のグレードで使用できる可能性があるか否か
    willAvailableForPremiumService() {
      return !!intersection(
        this.couponAvailablePremiumServiceIds,
        this.subscribedPremiumServiceIds
      ).length;
    },

    // 最低利用金額を下回っている場合の注意文を表示するか否か
    displayExceedMinimuUseAmountError() {
      return (
        !this.isExceedMinimumUseAmount &&
        !this.isLabelCoupon &&
        !this.isSupplierCoupon &&
        !this.isInStockOnlyCoupon
      );
    }
  },

  filters: {
    truncate: textUtil.truncate,
    staticImageUrl: urlUtil.staticImageUrl
  },

  methods: {
    // クーポンが選択、または非選択にされた場合の処理
    useCoupon() {
      if (this.canUseCoupon) {
        this.$emit('change', {
          discountId: this.discount._id,
          isUse: !this.isSelected
        });
      }
    }
  }
};
</script>
